import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, PlusCircle, Trash2, Search, ArrowLeft, GitCompareArrows, Users, Sparkles, Building2, MapPin, X, Play, RefreshCw, Download } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import 'leaflet/dist/leaflet.css';

export default function PointComparison() {
  const { id } = useParams();
  return id ? <ComparisonView id={id} /> : <ComparisonListAndNew />;
}

/* ============================================================
 * LIST + NEW
 * ============================================================ */
function ComparisonListAndNew() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMode, setNewMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/point-comparisons'); setItems(r.data || []); }
    catch { toast.error('Error cargando comparativas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="label-caps">Etapa 2 · Sprint 5</div>
      <div className="flex items-baseline justify-between mt-2 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">
            Comparador multi-punto
          </h1>
          <p className="text-slate-600 mt-1">Compara 2 a 10 ubicaciones lado a lado: movilidad · popularidad · foot-traffic · índices plaza.</p>
        </div>
        <Button onClick={() => setNewMode(!newMode)} data-testid="toggle-new-comparison" className="bg-blue-900 hover:bg-blue-800">
          {newMode ? <><X size={14} className="mr-1" />Cerrar</> : <><PlusCircle size={14} className="mr-1" />Nueva comparativa</>}
        </Button>
      </div>

      {newMode && <NewComparisonForm onCreated={(id) => { setNewMode(false); load(); nav(`/compare/${id}`); }} />}

      <div className="mt-6">
        <div className="label-caps mb-3">Comparativas guardadas</div>
        {loading ? <div className="text-slate-500 text-sm py-4">Cargando…</div> :
          items.length === 0 ? <div className="text-slate-400 text-sm py-4 border border-dashed border-slate-200 p-8 text-center">Aún no hay comparativas. Crea la primera arriba.</div> :
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(c => (
              <button key={c.id} onClick={() => nav(`/compare/${c.id}`)} data-testid={`comparison-${c.id}`}
                className="text-left bg-white border border-slate-200 p-4 hover:border-blue-900 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {c.industry ? `Giro: ${c.industry} · ` : ''}
                  {(c.places_input || []).length} lugares · {new Date(c.created_at).toLocaleDateString('es-MX')}
                </div>
              </button>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const s = { completed: 'bg-green-50 text-green-800', processing: 'bg-amber-50 text-amber-800', failed: 'bg-red-50 text-red-800' };
  const l = { completed: 'Completada', processing: 'Procesando…', failed: 'Falló' };
  return <span className={`text-xs px-2 py-0.5 font-semibold uppercase tracking-widest ${s[status] || 'bg-slate-100 text-slate-700'}`}>{l[status] || status}</span>;
};

function NewComparisonForm({ onCreated }) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true); setCandidates([]);
    try {
      const r = await api.get('/places/resolve', { params: { q } });
      setCandidates((r.data.candidates || []).filter(c => c.place_id));
      if (!r.data.candidates?.length) toast.error('Sin coincidencias');
    } catch { toast.error('Error en búsqueda'); }
    finally { setSearching(false); }
  };

  const add = (c) => {
    if (selected.length >= 10) return toast.error('Máximo 10 lugares');
    if (selected.find(x => x.place_id === c.place_id)) return toast.info('Ya está agregado');
    setSelected([...selected, { place_id: c.place_id, name: c.name, lat: c.lat, lon: c.lon, type_detected: c.type_detected }]);
    setCandidates([]); setQ('');
  };

  const remove = (pid) => setSelected(selected.filter(s => s.place_id !== pid));

  const submit = async () => {
    if (selected.length < 2) return toast.error('Agrega al menos 2 lugares');
    setSubmitting(true);
    try {
      const r = await api.post('/point-comparisons', { name: name || undefined, industry, places: selected });
      toast.success('Comparativa lanzada — procesando…');
      onCreated(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando comparativa'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-6" data-testid="new-comparison-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="label-caps mb-1 block">Nombre</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Plazas Polanco vs Roma vs Interlomas" data-testid="cmp-name" />
        </div>
        <div>
          <label className="label-caps mb-1 block">Giro (opcional)</label>
          <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="perfumería, restaurante, óptica…" data-testid="cmp-industry" />
        </div>
      </div>

      <form onSubmit={doSearch} className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca plaza, negocio, dirección…"
            className="pl-9 bg-white" data-testid="cmp-search-input" />
        </div>
        <Button type="submit" disabled={searching} data-testid="cmp-search-btn" className="bg-slate-900 hover:bg-slate-800">
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </Button>
      </form>

      {candidates.length > 0 && (
        <div className="border border-slate-200 mb-4 max-h-64 overflow-y-auto">
          {candidates.map((c, i) => (
            <button key={i} onClick={() => add(c)} data-testid={`cmp-candidate-${i}`}
              className="w-full text-left p-2 hover:bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-sm last:border-b-0">
              <MapPin size={12} className="text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-xs text-slate-500 truncate">{c.type_detected} · {c.address}</div>
              </div>
              <PlusCircle size={14} className="text-blue-900 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      <div className="mb-4">
        <div className="label-caps mb-2">Seleccionados ({selected.length}/10)</div>
        {selected.length === 0 ? (
          <div className="text-xs text-slate-400 py-2 border border-dashed border-slate-200 p-3 text-center">Agrega al menos 2 lugares.</div>
        ) : (
          <div className="space-y-1">
            {selected.map((s) => (
              <div key={s.place_id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-100">
                <MapPin size={12} className="text-blue-900 flex-shrink-0" />
                <div className="flex-1 min-w-0 text-sm">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-xs text-slate-500 ml-2">{s.type_detected}</span>
                </div>
                <button onClick={() => remove(s.place_id)} data-testid={`cmp-remove-${s.place_id}`} className="text-slate-400 hover:text-red-700">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Button onClick={submit} disabled={submitting || selected.length < 2}
        data-testid="cmp-submit-btn" className="bg-blue-900 hover:bg-blue-800">
        {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" />Lanzando…</> : <><Play size={14} className="mr-1" />Lanzar comparativa</>}
      </Button>
    </div>
  );
}

/* ============================================================
 * VIEW
 * ============================================================ */
function ComparisonView({ id }) {
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const r = await api.get(`/point-comparisons/${id}`); setDoc(r.data); }
    catch { toast.error('No se pudo cargar la comparativa'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (doc?.status === 'processing') {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [doc?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-12 text-slate-500">Cargando…</div>;
  if (!doc) return <div className="p-12 text-slate-500">Comparativa no encontrada</div>;

  const results = doc.results || [];
  const center = results.length && results[0].lat ? [results[0].lat, results[0].lon] : [23.6345, -102.5528];

  return (
    <div className="p-6 md:p-10 max-w-[1600px]">
      <button onClick={() => nav('/compare')} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={14} /> Volver
      </button>

      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-caps">Comparativa multi-punto</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mt-1">{doc.name}</h1>
          <div className="text-sm text-slate-600 mt-1">
            {doc.industry ? <>Giro: <strong>{doc.industry}</strong> · </> : ''}
            {results.length} lugares · {new Date(doc.created_at).toLocaleString('es-MX')}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={doc.status} />
          {doc.status === 'completed' && (
            <Button
              onClick={async () => {
                try {
                  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/point-comparisons/${id}/pdf`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('strategia_token')}` },
                  });
                  if (!r.ok) throw new Error();
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `StrategIA_Comparativa_${doc.name.replace(/\s+/g, '_')}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch { toast.error('Error descargando PDF'); }
              }}
              data-testid="cmp-pdf-btn"
              className="bg-blue-900 hover:bg-blue-800"
              size="sm"
            >
              <Download size={14} className="mr-1" />PDF ejecutivo
            </Button>
          )}
          <Button onClick={load} variant="ghost" size="sm" data-testid="cmp-refresh"><RefreshCw size={14} /></Button>
        </div>
      </div>

      {doc.status === 'processing' && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-4 flex items-center gap-2 text-sm text-amber-800">
          <Loader2 size={14} className="animate-spin" /> Procesando lugares en paralelo… (~15-30s)
        </div>
      )}

      {results.length > 0 && (
        <>
          {/* KPI Table */}
          <div className="bg-white border border-slate-200 overflow-x-auto mb-6" data-testid="kpi-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="p-3 label-caps">Lugar</th>
                  <th className="p-3 label-caps text-right">Movilidad</th>
                  <th className="p-3 label-caps text-right">Popularidad</th>
                  <th className="p-3 label-caps text-right">Foot-traffic/día</th>
                  <th className="p-3 label-caps text-right">Score Plaza</th>
                  <th className="p-3 label-caps text-right">Tenants</th>
                  <th className="p-3 label-caps text-right">Rating</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${i}`}>
                    <td className="p-3">
                      <div className="font-semibold text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.type_detected}</div>
                    </td>
                    <MetricCell v={r.kpis?.mobility_score} onClick={() => nav(`/search/place/${r.place_id}`)} />
                    <MetricCell v={r.kpis?.popularity_score} />
                    <MetricCell v={r.kpis?.foot_traffic_daily_mid} format="int" />
                    <MetricCell v={r.kpis?.plaza_score} />
                    <MetricCell v={r.kpis?.tenant_count} format="int" />
                    <MetricCell v={r.rating} format="rating" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bar charts side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <ChartPanel title="Popularidad digital" data={results.map(r => ({ name: r.name.slice(0, 18), value: r.kpis?.popularity_score || 0 }))} max={100} color="#7c3aed" />
            <ChartPanel title="Movilidad global" data={results.map(r => ({ name: r.name.slice(0, 18), value: r.kpis?.mobility_score || 0 }))} max={100} color="#0891b2" />
            <ChartPanel title="Foot-traffic estimado (mid/día)" data={results.map(r => ({ name: r.name.slice(0, 18), value: r.kpis?.foot_traffic_daily_mid || 0 }))} color="#15803d" />
            <ChartPanel title="Score plaza comercial" data={results.map(r => ({ name: r.name.slice(0, 18), value: r.kpis?.plaza_score || 0 }))} max={100} color="#1e3a8a" />
          </div>

          {/* Map */}
          <div className="bg-white border border-slate-200 overflow-hidden" style={{ height: 500 }}>
            <MapContainer center={center} zoom={results.length > 1 ? 6 : 13} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" attribution="&copy; Carto" />
              {results.map((r, i) => r.lat && (
                <CircleMarker key={i} center={[r.lat, r.lon]} radius={12}
                  pathOptions={{ color: '#1e3a8a', fillColor: '#3b82f6', fillOpacity: 0.85, weight: 2 }}>
                  <Popup>
                    <strong>{r.name}</strong><br />
                    Movilidad: {r.kpis?.mobility_score || '—'}<br />
                    Popularidad: {r.kpis?.popularity_score || '—'}<br />
                    Foot-traffic: {r.kpis?.foot_traffic_daily_mid?.toLocaleString('es-MX') || '—'}/día<br />
                    <a href={`/search/place/${r.place_id}`} className="text-blue-900 underline">Ver detalle →</a>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </>
      )}
    </div>
  );
}

const MetricCell = ({ v, format, onClick }) => {
  if (v == null || v === '' || (typeof v === 'number' && isNaN(v))) return <td className="p-3 text-right text-slate-300">—</td>;
  let display = v;
  if (format === 'int') display = Math.round(v).toLocaleString('es-MX');
  else if (format === 'rating') display = `★ ${Number(v).toFixed(1)}`;
  else if (typeof v === 'number') display = v.toFixed(1);
  return <td className={`p-3 text-right font-semibold ${onClick ? 'cursor-pointer hover:text-blue-900' : ''}`} onClick={onClick}>{display}</td>;
};

const ChartPanel = ({ title, data, max, color }) => (
  <div className="bg-white border border-slate-200 p-4">
    <div className="label-caps mb-3">{title}</div>
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 15, top: 0, bottom: 0 }}>
        <XAxis type="number" tick={{ fontSize: 10 }} domain={max ? [0, max] : ['auto', 'auto']} />
        <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 10 }} />
        <Tooltip formatter={(v) => (v || 0).toLocaleString('es-MX')} />
        <Bar dataKey="value">
          {data.map((_, i) => <Cell key={i} fill={color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);
